import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { BlogCategory, BlogPost } from '@/data/blog'

import { useState } from 'react'

import { Layout, Seo, PageHero } from '@/components/generals'
import { BlogCard } from '@/components/blog'
import { getBlogCategories, getBlogPosts } from '@/data/blog'

type BlogPageProps = {
    posts: BlogPost[]
    categories: BlogCategory[]
}

const BlogPage: FC<BlogPageProps> = ({ posts, categories }): ReactNode => {
    const [activeCategory, setActiveCategory] = useState<string | null>(null)

    const featured = posts.find(post => post.featured) ?? posts[0]
    const visiblePosts = activeCategory ? posts.filter(post => post.category === activeCategory) : posts

    return (
        <Layout>
            <Seo title='Blog' description='Stay updated with tech trends, product tips, and stories from the San Diego team.' />

            <PageHero
                badge='New'
                eyebrow='Introducing the Class app'
                eyebrowHref='/features'
                title={
                    <>
                        Stay updated with tech <span className='text-gradient'>trends.</span>
                    </>
                }
                description='Experience a new era of time, finance, and task management. Say goodbye to missed deadlines and hello to a stress-free, productive life.'
            />

            <div className='section'>
                <div className='container'>
                    {featured && (
                        <div className='animation-on-page-load'>
                            <div className='featured-post-wrapper'>
                                <div className='featured-post-list'>
                                    <div className='featured-post-item'>
                                        <BlogCard post={featured} featured />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='fade-in-on-scroll'>
                        <div className='folders-parent'>
                            <div className='filter-holder'>
                                <button
                                    type='button'
                                    onClick={() => setActiveCategory(null)}
                                    className={`category-item-link w-inline-block${activeCategory === null ? ' w--current' : ''}`}
                                >
                                    <div>All</div>
                                </button>
                                {categories.map(category => (
                                    <button
                                        key={category.slug}
                                        type='button'
                                        onClick={() => setActiveCategory(category.slug)}
                                        className={`category-item-link w-inline-block${activeCategory === category.slug ? ' w--current' : ''}`}
                                    >
                                        <div>{category.name}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='blog-items-holder'>
                        <div className='cms-wrapper'>
                            <div role='list' className='blog-grid w-dyn-items'>
                                {visiblePosts.map(post => (
                                    <BlogCard key={post.slug} post={post} />
                                ))}
                            </div>
                            {visiblePosts.length === 0 && (
                                <div className='w-dyn-empty'>
                                    <div>No items found.</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className='ellipses-holder _30-opacity'>
                    <div className='ellipse-right' />
                    <div className='ellipse-left' />
                </div>
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<BlogPageProps> = async () => {
    const [posts, categories] = await Promise.all([getBlogPosts(), getBlogCategories()])
    return { props: { posts, categories }, revalidate: 60 }
}

export default BlogPage
